import TuneIcon from '@mui/icons-material/Tune';
import { Tooltip, Stack, IconButton, ButtonGroup, Typography, Fab } from "@mui/material";
import { __APP_ROOT } from '../..';
import { useAppStore } from '../../store/app';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import DraggableDialog from '../DraggableDialog';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useCropperStore } from '@src/v3.0.0/store/cropper/cropper-store';
import CustomContentEditable from '../CustomContentEditable';
import l10n from '@src/v3.0.0/lib/l10n';

const filterProps = (allowedKeys, props) => {
  return Object.keys(props)
    .filter(key => allowedKeys.includes(key))
    .reduce((filteredProps, key) => {
      filteredProps[key] = props[key];
      return filteredProps;
    }, {});
};

export const CanvasTuningTool = () => {
  const [isOpen, setIsOpen] = useState(false);
  const appHook = useAppStore();
  const { getCropper, preset: cropperPreset, updatePreset: setCropperPreset } = useCropperStore();
  const [tunableParameters, setTunableParameters] = useState({});
  const [changedParam, setChangedParam] = useState({});
  
  /** ----------- Panel toggle -------------- */
  const toggleTuningPanel = () => {
    setIsOpen(!isOpen);
  };

  /** ----------- Parameters update on preset changed -------------- */
  const _ID = useRef();
  useEffect(() => {
    if (appHook.status === useAppStore.APP_STATUS_READY) {
      clearTimeout(_ID.current);
      _ID.current = setTimeout(() => {
        const reactCropper = getCropper(); 
        if (reactCropper) {
          setTunableParameters({
            // ...filterProps(['zoom'], cropperPreset),
            ...filterProps(['x', 'y'], reactCropper.getData()),
          });
        }
      }, 150);
      return () => clearTimeout(_ID.current);
    }
  }, [appHook.status, cropperPreset]);

  /** --------- Parameters manually submitted -------------- */
  const handleKeyDown = event => {
    
    if (event.key === "Enter") {
      event.preventDefault();
      
      const param_submit = event.target.dataset.param;
      const value = Number(event.target.innerText.trim());

      if (value !== NaN) {
        setTunableParameters((prev) => ({
          ...prev,
          [param_submit]: value
        }));

        setChangedParam({
          [param_submit]: value
        });
      }
    }
  }

  /** ----------- Parameters update on button [+|-] press -------------- */
  const onParameterSubmit = e => {
    const param_submit = e.target.dataset.param;
    const cmd = Number(e.target.dataset.cmd);
    const value = Number(tunableParameters[param_submit]) + cmd;
    
    if (value !== NaN) {
      setTunableParameters((prev) => ({
        ...prev,
        [param_submit]: value
      }));

      setChangedParam({
        [param_submit]: value
      });
    }
  };

  /** ----------- Parameter transforms -------------- */
  function handleCanvasOffset(value, paramSubmit) {
    const cropper = getCropper();
    const axis = paramsMapped[`_${paramSubmit}`];
    const inverseOffset = cropper._calculateInverseOffset(value, paramSubmit);
    cropper._moveCanvasByOffset({ [axis]: inverseOffset });
    return inverseOffset;
  };

  function handleCanvasZoom(rawValue, paramSubmit) {
    const cropper = getCropper();
    cropper._zoomRaw(rawValue / 100);
    return rawValue;
  };

  const paramsMapped = {
    _x: 'left',
    _y: 'top',
    _zoom: 'scale',
    x: handleCanvasOffset,
    y: handleCanvasOffset,
    zoom: handleCanvasZoom
  };

  const presetID = useRef();
  useEffect(() => {
    if (changedParam) {
      const paramSubmit = Object.keys(changedParam)[0];
      const value = Object.values(changedParam)[0];
      const paramCallback = paramsMapped[paramSubmit];
      if (paramCallback) {
        const valueMapped = typeof paramCallback === 'function' ? paramCallback(value, paramSubmit) : value;
        clearTimeout(presetID.current);
        presetID.current = setTimeout(() => {
          setCropperPreset({
            [paramsMapped[`_${paramSubmit}`]]: valueMapped
          });
        }, 500);
        return () => {
          clearTimeout(presetID.current);
        }
      }
    }
  }, [changedParam]);

  if (!l10n.u_admin) {
    return null;
  }

  return (
    <>
      <Tooltip title={"Affina i parametri della stampa"} placement="top">
        <Fab onClick={toggleTuningPanel}>
          <TuneIcon />
        </Fab>
      </Tooltip>
      {__APP_ROOT.shadowRoot.querySelector('#cp-view-main') && createPortal(
        <DraggableDialog
          IconComponent={TuneIcon}
          title={'Affina Stampa'}
          description={
            <Typography fontSize={'small'}>
              I parametri sono relativi al ritaglio dell'immagine.
            </Typography>
          }
          isOpen={isOpen && Object.keys(tunableParameters).length > 0}
          close={toggleTuningPanel}
        >
          <Stack spacing={2} pt={1}>
            {Object.keys(tunableParameters).map((parameter, key) => {
              return (
                <Stack direction={'row'} alignItems={'center'} gap={1} key={key}>
                  <Stack direction={'row'} gap={1} width={'100%'}>
                    <Typography width={'4ch'} textAlign={'right'}>{parameter}</Typography>
                    <CustomContentEditable 
                      isNumerical
                      value={tunableParameters[parameter].toFixed(2)}
                      slotProps={{
                        contentEditable: {
                          'data-param': parameter,
                          onKeyDown: handleKeyDown,
                        }
                      }}
                    />
                  </Stack>
                  <ButtonGroup>
                    <IconButton size='small' onClick={onParameterSubmit} data-param={parameter} data-cmd={-1}>
                      <AddIcon fontSize='small' style={{ pointerEvents: 'none' }} />
                    </IconButton>
                    <IconButton size='small' onClick={onParameterSubmit} data-param={parameter} data-cmd={1}>
                      <RemoveIcon fontSize='small' style={{ pointerEvents: 'none' }} />
                    </IconButton>
                  </ButtonGroup>
                </Stack>
              )
            })}
            {/* <Button onClick={getData}>Data</Button>
            <div style={{ maxWidth: '180px', wordBreak: 'break-all' }} dangerouslySetInnerHTML={{ __html: JSON.stringify(cropeprData)}} /> */}
          </Stack>
        </DraggableDialog>
        ,
        __APP_ROOT.shadowRoot.querySelector('#cp-view-main')
      )}
    </>
  );
}